/**
 * Static writer: applies an OutputPlan's written block to the sheet through a
 * SheetPort, clears what the previous result left behind (the footprint), and
 * sets Excel's built-in short-date format on date cells (spec §5.1, §5.3 rule 7).
 * Any other cell keeps the number format it has: LSEG never resets one (the
 * LSEG-calculated originals in migrate/ keep 0.00 / 0.00% on TR and RHistory
 * formula cells and on Dest-written numbers).
 * Pure of Office globals; excelPort.ts supplies the Excel-backed port.
 */
import { Footprint, FootprintStore } from "./footprints";
import { Cell, OutputPlan } from "./layout";

/** A value to write: never empty (empty cells are cleared). `error`: v is the error's literal ("#N/A"). */
export interface WriteCell {
  v: string | number | boolean;
  error?: boolean;
}

export interface SheetPort {
  /** Clears contents only (formats stay). */
  clear(sheet: string, row: number, col: number, rows: number, cols: number): Promise<void>;
  /** Writes typed values: a string is stored as that text, never parsed as input. */
  write(sheet: string, row: number, col: number, cells: WriteCell[][]): Promise<void>;
  /** Sets one invariant number format on a rectangle (only date and time formats are ever set). */
  format(sheet: string, row: number, col: number, rows: number, cols: number, format: string): Promise<void>;
}

type Pos = { row: number; col: number };
type Rect = { row: number; col: number; rows: number; cols: number };

/**
 * Excel's built-in short date (format id 14). Set through the invariant
 * numberFormat, Excel displays it in the running locale (fr-FR: jj/mm/aaaa).
 */
export const SHORT_DATE = "m/d/yyyy";

/** Date with time of day, as LSEG writes tick timestamps (golden w_rh_tick_value_nbrows). */
export const SHORT_DATETIME = "m/d/yyyy h:mm";

/** Time of day, as LSEG writes CF_TIME (golden tr_cf_time_alone). */
export const TIME_OF_DAY = "h:mm:ss AM/PM";

/** The number format a cell gets: time of day, date-time or date; null (any other cell) = left as it is. */
export function numberFormatOf(cell: { date?: boolean; datetime?: boolean; time?: boolean } | undefined): string | null {
  if (cell?.time) return TIME_OF_DAY;
  if (cell?.datetime) return SHORT_DATETIME;
  return cell?.date ? SHORT_DATE : null;
}

/** Coalescing window for repeated applies of the same key. */
const WINDOW_MS = 50;

/**
 * Grace period between a formula's cancellation and the clearing of its
 * footprint. Excel cancels and re-invokes a streaming formula whenever one of
 * its arguments changes, and a Dest argument (or a cell the formula reads
 * inside its own footprint) changes because of the writer's own writes: the
 * re-invocation cancels the pending release, so the unchanged rewrite stays a
 * no-op instead of a clear/refetch/rewrite loop. A deleted formula is cleared
 * once the grace period ends.
 */
export const RELEASE_GRACE_MS = 1000;

type Job =
  | { kind: "apply"; sheet: string; formula: Pos; plan: OutputPlan }
  | { kind: "release" };

interface Pending {
  job: Job;
  waiters: { resolve: () => void; reject: (e: unknown) => void }[];
}

const inRect = (r: Rect, row: number, col: number): boolean =>
  row >= r.row && row < r.row + r.rows && col >= r.col && col < r.col + r.cols;

/**
 * Splits the cells of `area` for which `keep` holds into rectangles: runs per
 * row, merged downward while consecutive rows have the same run.
 */
function rectsWhere(area: Rect, keep: (row: number, col: number) => boolean): Rect[] {
  const done: Rect[] = [];
  let open = new Map<string, Rect>();
  for (let row = area.row; row < area.row + area.rows; row++) {
    const next = new Map<string, Rect>();
    let col = area.col;
    const end = area.col + area.cols;
    while (col < end) {
      if (!keep(row, col)) { col++; continue; }
      const start = col;
      while (col < end && keep(row, col)) col++;
      const id = `${start}:${col}`;
      const prev = open.get(id);
      if (prev) { prev.rows++; open.delete(id); next.set(id, prev); }
      else next.set(id, { row, col: start, rows: 1, cols: col - start });
    }
    open.forEach((r) => done.push(r));
    open = next;
  }
  open.forEach((r) => done.push(r));
  return done;
}

/**
 * The formula cell a key names: keys are `${sheet}!${address}`, followed by
 * `#${callId}` when they name one call of the cell (address in A1 form, `$`
 * allowed); the sheet name may itself contain "!", so split on the last one.
 */
export function formulaCellOf(key: string): { sheet: string; row: number; col: number } | null {
  const bang = key.lastIndexOf("!");
  if (bang < 0) return null;
  const m = /^\$?([A-Za-z]{1,3})\$?(\d+)(?:#[0-9A-Za-z]+)?$/.exec(key.slice(bang + 1));
  if (!m) return null;
  let col = 0;
  for (const ch of m[1].toUpperCase()) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { sheet: key.slice(0, bang), row: Number(m[2]) - 1, col: col - 1 };
}

/** The part of a plan the writer acts on (formulaValue is returned by the function, not written). */
function signature(sheet: string, formula: Pos, plan: OutputPlan): string {
  return JSON.stringify([
    sheet, formula.row, formula.col, plan.formulaIsDate, !!plan.formulaIsDateTime, !!plan.formulaIsTime, !!plan.formulaIsError, plan.writes,
  ]);
}

/** Two keys naming the same formula cell (two calls of one cell). */
function sameCell(a: string, b: string): boolean {
  const x = formulaCellOf(a);
  const y = formulaCellOf(b);
  return !!x && !!y && x.sheet === y.sheet && x.row === y.row && x.col === y.col;
}

export class StaticWriter {
  private pending = new Map<string, Pending>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private chain: Promise<void> = Promise.resolve();
  /** Last applied signature per key (in memory; empty after a reload). */
  private last = new Map<string, { sig: string }>();
  /** Pending deferred releases per key. */
  private releases = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private readonly port: SheetPort,
    private readonly store: FootprintStore,
  ) {}

  apply(key: string, sheet: string, formula: Pos, plan: OutputPlan): Promise<void> {
    this.cancelRelease(key);
    return this.enqueue(key, { kind: "apply", sheet, formula: { ...formula }, plan });
  }

  /** Clear and forget a footprint now. */
  release(key: string): Promise<void> {
    this.cancelRelease(key);
    return this.enqueue(key, { kind: "release" });
  }

  /**
   * Release `key` after `ms` (cancelled formula) unless cancelRelease or an
   * apply for the same key comes first. Footprint and last signature are
   * kept until the release runs.
   */
  scheduleRelease(key: string, ms: number = RELEASE_GRACE_MS): void {
    this.cancelRelease(key);
    this.releases.set(
      key,
      setTimeout(() => {
        this.releases.delete(key);
        this.release(key).catch((e) => console.error("EAV: clearing results failed", e));
      }, ms),
    );
  }

  /** Drop a pending scheduleRelease for `key` (the formula was invoked again). */
  cancelRelease(key: string): void {
    const t = this.releases.get(key);
    if (t === undefined) return;
    clearTimeout(t);
    this.releases.delete(key);
  }

  private enqueue(key: string, job: Job): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const entry = this.pending.get(key);
      if (entry) {
        entry.job = job; // keep only the last plan
        entry.waiters.push({ resolve, reject });
      } else {
        this.pending.set(key, { job, waiters: [{ resolve, reject }] });
      }
      if (!this.timer) this.timer = setTimeout(() => this.flush(), WINDOW_MS);
    });
  }

  private flush(): void {
    this.timer = null;
    const batch = [...this.pending.entries()];
    this.pending.clear();
    // Batches run one after another so footprints are never read mid-update.
    this.chain = this.chain.then(() => this.runBatch(batch));
  }

  private async runBatch(batch: [string, Pending][]): Promise<void> {
    const results: { waiters: Pending["waiters"]; error?: unknown }[] = [];
    const inBatch = new Set(batch.map(([key]) => key));
    let dirty = false;
    for (const [key, { job, waiters }] of batch) {
      try {
        if (job.kind === "apply" ? await this.applyNow(key, job, inBatch) : await this.releaseNow(key)) dirty = true;
        results.push({ waiters });
      } catch (error) {
        results.push({ waiters, error });
      }
    }
    let saveError: unknown;
    if (dirty) {
      try { await this.store.save(); } catch (e) { saveError = e; }
    }
    for (const { waiters, error } of results) {
      const err = error ?? saveError;
      waiters.forEach((w) => (err !== undefined ? w.reject(err) : w.resolve()));
    }
  }

  /** Returns true when the store changed. */
  private async applyNow(key: string, job: Extract<Job, { kind: "apply" }>, inBatch: Set<string>): Promise<boolean> {
    const { sheet, formula, plan } = job;
    const sig = signature(sheet, formula, plan);
    if (this.last.get(key)?.sig === sig) return false;

    const cells: Cell[][] = plan.writes?.cells ?? [];
    const width = cells.reduce((m, r) => Math.max(m, r.length), 0);
    // Blocks are written on the formula's own sheet (a Dest on another one is
    // refused, spec §5.3 rule 3). An old footprint may still sit on another
    // sheet (written before that rule): clearOld clears it on its own sheet.
    const next: Footprint | undefined =
      plan.writes && cells.length && width
        ? { sheet, row: plan.writes.anchor.row, col: plan.writes.anchor.col, rows: cells.length, cols: width }
        : undefined;

    const ops: Promise<void>[] = [];
    const clearOld = (k: string, old: Footprint) => {
      for (const r of this.clearable(k, old, old.sheet === sheet ? formula : null, next)) {
        ops.push(this.port.clear(old.sheet, r.row, r.col, r.rows, r.cols));
      }
    };
    // 0. Footprints stored for this formula cell under another call's key that
    // nothing has claimed since the workbook was opened: the formula changed
    // while the add-in was not running (a key carries its call's arguments),
    // so this call takes them over and clears what it does not rewrite. A key
    // in this batch, or applied since load, is a live sibling call: left alone.
    const stale = this.store.all().filter(([k]) => k !== key && !this.last.has(k) && !inBatch.has(k) && sameCell(k, key));
    stale.forEach(([k]) => this.store.set(k, undefined));
    stale.forEach(([k, fp]) => clearOld(k, fp));
    // 1. Clear the old footprint where the new one doesn't reach.
    const old = this.store.get(key);
    if (old) clearOld(key, old);
    // 2. The new block, skip placeholders and the formula cell aside: values,
    // empty cells cleared (never written as ""), and the date formats.
    if (next) {
      const cellAt = (row: number, col: number): Cell | undefined => cells[row - next.row]?.[col - next.col];
      const target = (row: number, col: number): Cell | undefined => {
        const cell = cellAt(row, col);
        if (!cell || cell.skip || (row === formula.row && col === formula.col)) return undefined;
        return cell;
      };
      for (const r of rectsWhere(next, (row, col) => target(row, col)?.v === null)) {
        ops.push(this.port.clear(sheet, r.row, r.col, r.rows, r.cols));
      }
      const valued = (row: number, col: number): boolean => {
        const cell = target(row, col);
        return cell !== undefined && cell.v !== null;
      };
      for (const r of rectsWhere(next, valued)) {
        const values: WriteCell[][] = [];
        for (let i = 0; i < r.rows; i++) {
          const line: WriteCell[] = [];
          for (let j = 0; j < r.cols; j++) {
            const cell = cellAt(r.row + i, r.col + j)!;
            line.push(cell.error ? { v: cell.v!, error: true } : { v: cell.v! });
          }
          values.push(line);
        }
        ops.push(this.port.write(sheet, r.row, r.col, values));
      }
      for (const fmt of [SHORT_DATE, SHORT_DATETIME, TIME_OF_DAY]) {
        for (const r of rectsWhere(next, (row, col) => numberFormatOf(target(row, col)) === fmt)) {
          ops.push(this.port.format(sheet, r.row, r.col, r.rows, r.cols, fmt));
        }
      }
    }
    // 3. The formula cell gets a format only when it holds a date.
    const formulaFormat = numberFormatOf({ date: plan.formulaIsDate, datetime: plan.formulaIsDateTime, time: plan.formulaIsTime });
    if (formulaFormat) ops.push(this.port.format(sheet, formula.row, formula.col, 1, 1, formulaFormat));
    // All of a key's operations are issued together so the port can batch them.
    await Promise.all(ops);

    this.store.set(key, next);
    this.last.set(key, { sig });
    return true;
  }

  private async releaseNow(key: string): Promise<boolean> {
    const old = this.store.get(key);
    this.last.delete(key);
    if (!old) return false;
    await Promise.all(this.clearable(key, old, null, undefined).map((r) => this.port.clear(old.sheet, r.row, r.col, r.rows, r.cols)));
    this.store.set(key, undefined);
    return true;
  }

  /**
   * Cells of `old` not in `next`, not covered by another key's footprint, and
   * not a formula cell: this key's (`formula`, or derived from the key) nor
   * any other known key's (derived from its key).
   */
  private clearable(key: string, old: Footprint, formula: Pos | null, next: Footprint | undefined): Rect[] {
    const others = this.store.all().filter(([k, fp]) => k !== key && fp.sheet === old.sheet).map(([, fp]) => fp);
    const keys = new Set<string>([key, ...this.store.all().map(([k]) => k), ...this.last.keys()]);
    const formulaCells = new Set<string>();
    keys.forEach((k) => {
      const f = formulaCellOf(k);
      if (f && f.sheet === old.sheet) formulaCells.add(`${f.row},${f.col}`);
    });
    if (formula) formulaCells.add(`${formula.row},${formula.col}`);
    const sameSheetNext = next && next.sheet === old.sheet ? next : undefined;
    return rectsWhere(old, (row, col) =>
      !formulaCells.has(`${row},${col}`) &&
      !(sameSheetNext && inRect(sameSheetNext, row, col)) &&
      !others.some((fp) => inRect(fp, row, col)),
    );
  }
}
